import React, { useState, useRef, useEffect } from 'react';
import { trackLead } from '../utils/tracking';
import styled from 'styled-components';
import {
  FiCpu, FiList, FiEdit3, FiMapPin, FiCamera, FiUser, FiSend, FiCheckCircle, FiHome,
} from 'react-icons/fi';
import { api } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { WhatsAppService } from '../utils/whatsapp';
import { useScrollToTop } from '../hooks/useScrollToTop';
import Seo from '../components/Seo';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.lg};
  min-height: 80vh;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(2rem, 4vw, 2.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.foreground};
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  line-height: 1.6;
`;

const MainCard = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing.xxxl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ExampleBox = styled.div`
  background: ${({ theme }) => theme.colors.primarySoft};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ExampleTitle = styled.h4`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ExampleText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.8;
  margin: 0;
  font-style: italic;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.lg};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 200px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
  
  &::placeholder {
    color: #6b7280;
  }
`;

const PhotoUploadArea = styled.div`
  border: 3px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}05;
  }
  
  input[type="file"] {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
`;

const PhotoIcon = styled.div`
  color: ${({ theme }) => theme.colors.neutral[400]};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  justify-content: center;
`;

const PhotoText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const PhotoCount = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const PreviewImage = styled.div<{ src: string }>`
  aspect-ratio: 1;
  background: url(${props => props.src}) center/cover;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  position: relative;
  
  &:hover button {
    opacity: 1;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const ClientSection = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const Select = styled.select`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const SubmitButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xxl};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #065f46;
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  font-size: 1.125rem;
  line-height: 1.6;
  box-shadow: ${({ theme }) => theme.shadows.md};
  white-space: pre-line;
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #991b1b;
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  font-size: 1.125rem;
`;

const CharCount = styled.div`
  text-align: right;
  color: #6b7280;
  font-size: 0.875rem;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxxl};
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  max-width: 600px;
  margin: 0 auto;
`;

const SuccessIcon = styled.div`
  width: 92px;
  height: 92px;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
  border-radius: 50%;
  background: #dcfce7;
  color: ${({ theme }) => theme.colors.success};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SuccessTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: 1.75rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SuccessDetails = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin: ${({ theme }) => theme.spacing.xl} 0;
  text-align: left;
  line-height: 1.8;
`;

const NewPropertyButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

const ModeToggle = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border: 2px solid ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $active }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $active }) => $active ? 'white' : theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const AdvertisePropertyPage: React.FC = () => {
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [clientData, setClientData] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'owner'
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [useAIMode, setUseAIMode] = useState(true); // Toggle para modo AI ou formulário
  const [submissionSuccess, setSubmissionSuccess] = useState(false); // Controla se foi enviado com sucesso
  const [propertyData, setPropertyData] = useState({
    title: '',
    type: 'casa',
    price: '',
    area: '',
    bedrooms: '',
    bathrooms: '',
    parkingSpaces: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: 'SP',
    zipcode: '',
    features: '',
    notes: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  useScrollToTop();

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newImages = [...images, ...files].slice(0, 10); // Limit to 10 images
    setImages(newImages);
    
    // Create previews
    const newPreviews: string[] = [];
    newImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result as string);
        if (newPreviews.length === newImages.length) {
          setImagePreviews(newPreviews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (useAIMode && !description.trim()) {
      setErrorMessage('Por favor, descreva o imóvel antes de enviar.');
      return;
    }
    
    if (!useAIMode && !propertyData.title.trim()) {
      setErrorMessage('Por favor, preencha pelo menos o título do imóvel.');
      return;
    }
    
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const formData = new FormData();
      
      if (useAIMode) {
        // Modo AI - enviar apenas descrição
        formData.append('description', description);
      } else {
        // Modo formulário - enviar dados estruturados
        formData.append('title', propertyData.title);
        formData.append('description', propertyData.notes || propertyData.title);
        formData.append('type', propertyData.type);
        formData.append('property_status', 'pending_approval');
        formData.append('source', 'public_form');
        formData.append('price', propertyData.price || '0');
        formData.append('area', propertyData.area || '0');
        formData.append('bedrooms', propertyData.bedrooms || '0');
        formData.append('bathrooms', propertyData.bathrooms || '0');
        formData.append('parking_spaces', propertyData.parkingSpaces || '0');
        formData.append('zipcode', propertyData.zipcode);
        formData.append('street', propertyData.street);
        formData.append('number', propertyData.number);
        formData.append('complement', propertyData.complement || '');
        formData.append('neighborhood', propertyData.neighborhood);
        formData.append('city', propertyData.city);
        formData.append('state', propertyData.state);
        formData.append('features', propertyData.features || '');
        formData.append('notes', propertyData.notes || '');
      }
      
      // Client data (comum para ambos os modos)
      formData.append('owner_name', clientData.name);
      formData.append('owner_email', clientData.email);
      formData.append('owner_phone', clientData.phone);
      formData.append('lead_type', clientData.type);
      
      // Add images
      images.forEach(image => {
        formData.append('images', image);
      });

      const endpoint = useAIMode ? '/properties/submit-for-approval' : '/properties/advertise';
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Mostrar resposta de sucesso
      setSubmissionSuccess(true);
      setErrorMessage(''); // Limpar qualquer mensagem de erro
      
      if (useAIMode && response.data.extracted_data) {
        const extractedData = response.data.extracted_data;
        let successMsg = 'Imóvel processado com sucesso! Nossa IA identificou:\n';
        if (extractedData.title) successMsg += `\n✓ ${extractedData.title}`;
        if (extractedData.type && extractedData.type !== 'a_definir') successMsg += `\n✓ Tipo: ${extractedData.type}`;
        if (extractedData.price > 0) successMsg += `\n✓ Valor: R$ ${extractedData.price.toLocaleString('pt-BR')}`;
        if (extractedData.location && extractedData.location !== 'A definir') successMsg += `\n✓ Localização: ${extractedData.location}`;
        if (extractedData.features_count > 0) successMsg += `\n✓ ${extractedData.features_count} características identificadas`;
        successMsg += '\n\nNossa equipe revisará as informações e entrará em contato em até 24 horas.';
        setSuccessMessage(successMsg);
      } else {
        setSuccessMessage('Imóvel enviado com sucesso! Nossa equipe analisará as informações e entrará em contato em até 24 horas.');
      }
      
      trackLead('advertise_form');

      // Show WhatsApp option
      const extractedData = useAIMode && response.data.extracted_data ? response.data.extracted_data : {};
      const leadData = {
        name: clientData.name,
        email: clientData.email,
        phone: clientData.phone,
        message: `Nova submissão de imóvel: ${extractedData.title || 'Imóvel para análise'}`,
        propertyTitle: extractedData.title || 'Imóvel para análise'
      };
      
      setTimeout(() => {
        WhatsAppService.showWhatsAppOption(leadData);
      }, 2000);
      
      // Reset form
      setDescription('');
      setImages([]);
      setImagePreviews([]);
      setClientData({
        name: '',
        email: '',
        phone: '',
        type: 'owner'
      });
      setPropertyData({
        title: '',
        type: 'casa',
        price: '',
        area: '',
        bedrooms: '',
        bathrooms: '',
        parkingSpaces: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: 'SP',
        zipcode: '',
        features: '',
        notes: ''
      });
      
    } catch (error) {
      console.error('Erro ao enviar imóvel:', error);
      setErrorMessage('Erro ao enviar imóvel. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const exampleDescription = `Casa com 3 quartos no Jardim América

Excelente casa em condomínio fechado, com 150m² de área construída em terreno de 300m².

Possui:
- 3 quartos sendo 1 suíte
- Sala ampla com 2 ambientes
- Cozinha planejada
- Área gourmet com churrasqueira
- 2 vagas de garagem cobertas
- Piscina

Localizada em rua tranquila, próximo a escolas, supermercados e com fácil acesso às principais vias.

Aceita financiamento. Valor: R$ 650.000`;

  const handleNewProperty = () => {
    setSubmissionSuccess(false);
    setSuccessMessage('');
    setErrorMessage('');
    // Resetar formulário já foi feito após o sucesso
  };

  return (
    <>
      <Seo
        title="Anuncie seu imóvel"
        description="Cadastre seu imóvel para avaliação e conte com assessoria especializada na divulgação e negociação."
        canonicalPath="/anunciar"
      />
      <Header />
      <WhatsAppButton />
      <Container>
        <Title>Anuncie seu Imóvel</Title>
        <Subtitle>
          Processo simplificado: descreva seu imóvel, adicione fotos e pronto! 
          Nossa equipe cuidará de todo o resto.
        </Subtitle>

        {submissionSuccess ? (
          <SuccessContainer>
            <SuccessIcon><FiCheckCircle size={56} /></SuccessIcon>
            <SuccessTitle>Imóvel Enviado com Sucesso!</SuccessTitle>
            {successMessage && (
              <SuccessDetails>
                {successMessage}
              </SuccessDetails>
            )}
            <NewPropertyButton onClick={handleNewProperty}>
              <FiHome size={18} /> Cadastrar novo imóvel
            </NewPropertyButton>
          </SuccessContainer>
        ) : (
          <>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

            <ModeToggle>
          <ToggleButton
            type="button"
            $active={useAIMode}
            onClick={() => setUseAIMode(true)}
          >
            <FiCpu size={18} /> Modo inteligente (IA)
          </ToggleButton>
          <ToggleButton
            type="button"
            $active={!useAIMode}
            onClick={() => setUseAIMode(false)}
          >
            <FiList size={18} /> Modo formulário
          </ToggleButton>
        </ModeToggle>

        <MainCard>
          <form onSubmit={handleSubmit}>
            <Section>
              <SectionTitle>
                <FiEdit3 size={18} /> {useAIMode ? 'Descreva seu imóvel' : 'Dados do imóvel'}
              </SectionTitle>
              
              {useAIMode ? (
                <>
                  <ExampleBox>
                    <ExampleTitle>
                      <FiEdit3 size={15} /> Exemplo de descrição
                    </ExampleTitle>
                    <ExampleText>{exampleDescription}</ExampleText>
                  </ExampleBox>
                  
                  <TextArea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Comece com um título chamativo, depois descreva as características do imóvel, localização, valor..."
                    required
                  />
                  <CharCount>
                    {description.length} caracteres
                  </CharCount>
                </>
              ) : (
                <>
                  <FormRow>
                    <FormGroup>
                      <Label>Título do anúncio *</Label>
                      <Input
                        type="text"
                        value={propertyData.title}
                        onChange={(e) => setPropertyData({...propertyData, title: e.target.value})}
                        placeholder="Ex: Casa 3 quartos no Jardim América"
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Tipo de imóvel *</Label>
                      <Select
                        value={propertyData.type}
                        onChange={(e) => setPropertyData({...propertyData, type: e.target.value})}
                        required
                      >
                        <option value="casa">Casa</option>
                        <option value="apartamento">Apartamento</option>
                        <option value="terreno">Terreno</option>
                        <option value="comercial">Comercial</option>
                        <option value="chacara">Chácara</option>
                        <option value="galpao">Galpão</option>
                      </Select>
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Preço</Label>
                      <Input
                        type="text"
                        value={propertyData.price}
                        onChange={(e) => setPropertyData({...propertyData, price: e.target.value})}
                        placeholder="Ex: 650000"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Área (m²)</Label>
                      <Input
                        type="text"
                        value={propertyData.area}
                        onChange={(e) => setPropertyData({...propertyData, area: e.target.value})}
                        placeholder="Ex: 150"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Quartos</Label>
                      <Input
                        type="number"
                        value={propertyData.bedrooms}
                        onChange={(e) => setPropertyData({...propertyData, bedrooms: e.target.value})}
                        placeholder="0"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Banheiros</Label>
                      <Input
                        type="number"
                        value={propertyData.bathrooms}
                        onChange={(e) => setPropertyData({...propertyData, bathrooms: e.target.value})}
                        placeholder="0"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Vagas</Label>
                      <Input
                        type="number"
                        value={propertyData.parkingSpaces}
                        onChange={(e) => setPropertyData({...propertyData, parkingSpaces: e.target.value})}
                        placeholder="0"
                      />
                    </FormGroup>
                  </FormRow>

                  <SectionTitle style={{ marginTop: '2rem', marginBottom: '1rem' }}>
                    <FiMapPin size={18} /> Endereço
                  </SectionTitle>

                  <FormRow>
                    <FormGroup>
                      <Label>CEP</Label>
                      <Input
                        type="text"
                        value={propertyData.zipcode}
                        onChange={(e) => setPropertyData({...propertyData, zipcode: e.target.value})}
                        placeholder="00000-000"
                      />
                    </FormGroup>
                    <FormGroup style={{ gridColumn: 'span 2' }}>
                      <Label>Rua</Label>
                      <Input
                        type="text"
                        value={propertyData.street}
                        onChange={(e) => setPropertyData({...propertyData, street: e.target.value})}
                        placeholder="Nome da rua"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Número</Label>
                      <Input
                        type="text"
                        value={propertyData.number}
                        onChange={(e) => setPropertyData({...propertyData, number: e.target.value})}
                        placeholder="123"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Complemento</Label>
                      <Input
                        type="text"
                        value={propertyData.complement}
                        onChange={(e) => setPropertyData({...propertyData, complement: e.target.value})}
                        placeholder="Apto, Bloco..."
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Bairro</Label>
                      <Input
                        type="text"
                        value={propertyData.neighborhood}
                        onChange={(e) => setPropertyData({...propertyData, neighborhood: e.target.value})}
                        placeholder="Nome do bairro"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup style={{ flex: 2 }}>
                      <Label>Cidade</Label>
                      <Input
                        type="text"
                        value={propertyData.city}
                        onChange={(e) => setPropertyData({...propertyData, city: e.target.value})}
                        placeholder="São Paulo"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Estado</Label>
                      <Select
                        value={propertyData.state}
                        onChange={(e) => setPropertyData({...propertyData, state: e.target.value})}
                      >
                        <option value="AC">AC</option>
                        <option value="AL">AL</option>
                        <option value="AP">AP</option>
                        <option value="AM">AM</option>
                        <option value="BA">BA</option>
                        <option value="CE">CE</option>
                        <option value="DF">DF</option>
                        <option value="ES">ES</option>
                        <option value="GO">GO</option>
                        <option value="MA">MA</option>
                        <option value="MT">MT</option>
                        <option value="MS">MS</option>
                        <option value="MG">MG</option>
                        <option value="PA">PA</option>
                        <option value="PB">PB</option>
                        <option value="PR">PR</option>
                        <option value="PE">PE</option>
                        <option value="PI">PI</option>
                        <option value="RJ">RJ</option>
                        <option value="RN">RN</option>
                        <option value="RS">RS</option>
                        <option value="RO">RO</option>
                        <option value="RR">RR</option>
                        <option value="SC">SC</option>
                        <option value="SP">SP</option>
                        <option value="SE">SE</option>
                        <option value="TO">TO</option>
                      </Select>
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Label>Características (separadas por vírgula)</Label>
                    <TextArea
                      value={propertyData.features}
                      onChange={(e) => setPropertyData({...propertyData, features: e.target.value})}
                      placeholder="Piscina, Churrasqueira, Área gourmet, Portaria 24h..."
                      style={{ minHeight: '80px' }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Observações adicionais</Label>
                    <TextArea
                      value={propertyData.notes}
                      onChange={(e) => setPropertyData({...propertyData, notes: e.target.value})}
                      placeholder="Informações adicionais sobre o imóvel..."
                      style={{ minHeight: '100px' }}
                    />
                  </FormGroup>
                </>
              )}
            </Section>

            <Section>
              <SectionTitle>
                <FiCamera size={18} /> Adicione fotos
              </SectionTitle>
              
              <PhotoUploadArea onClick={() => fileInputRef.current?.click()}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  onClick={(e) => e.stopPropagation()}
                />
                <PhotoIcon><FiCamera size={34} /></PhotoIcon>
                <PhotoText>
                  Clique ou arraste fotos do imóvel aqui
                </PhotoText>
                <PhotoText style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Máximo 10 fotos • JPG, PNG
                </PhotoText>
                {images.length > 0 && (
                  <PhotoCount>
                    {images.length} {images.length === 1 ? 'foto selecionada' : 'fotos selecionadas'}
                  </PhotoCount>
                )}
              </PhotoUploadArea>
              
              {imagePreviews.length > 0 && (
                <PreviewGrid>
                  {imagePreviews.map((preview, index) => (
                    <PreviewImage key={index} src={preview}>
                      <RemoveButton
                        type="button"
                        onClick={() => removeImage(index)}
                      >
                        ×
                      </RemoveButton>
                    </PreviewImage>
                  ))}
                </PreviewGrid>
              )}
            </Section>

            <Section>
              <SectionTitle>
                <FiUser size={18} /> Seus dados
              </SectionTitle>
              
              <ClientSection>
                <FormRow>
                  <FormGroup>
                    <Label>Seu nome *</Label>
                    <Input
                      type="text"
                      value={clientData.name}
                      onChange={(e) => setClientData({...clientData, name: e.target.value})}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Você é *</Label>
                    <Select
                      value={clientData.type}
                      onChange={(e) => setClientData({...clientData, type: e.target.value})}
                      required
                    >
                      <option value="owner">Proprietário</option>
                      <option value="partner_broker">Corretor Parceiro</option>
                      <option value="real_estate">Imobiliária</option>
                      <option value="construction_company">Construtora</option>
                      <option value="referral">Indicação</option>
                    </Select>
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label>E-mail *</Label>
                    <Input
                      type="email"
                      value={clientData.email}
                      onChange={(e) => setClientData({...clientData, email: e.target.value})}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>WhatsApp *</Label>
                    <Input
                      type="tel"
                      value={clientData.phone}
                      onChange={(e) => setClientData({...clientData, phone: e.target.value})}
                      placeholder="(11) 98765-4321"
                      required
                    />
                  </FormGroup>
                </FormRow>
              </ClientSection>
            </Section>

            <SubmitButton type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span>⏳</span> Enviando...
                </>
              ) : (
                <>
                  <FiSend size={18} /> Enviar para análise
                </>
              )}
            </SubmitButton>
          </form>
        </MainCard>
        </>
        )}
      </Container>
      <Footer />
    </>
  );
};

export default AdvertisePropertyPage;
